import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useGetBadgeSummaryQuery, useGetUserBadgesQuery } from 'store/services/badge/badgeApi';
import { Badge, TIER_FROM_LEVEL, UserBadge } from 'types/interfaces/badge';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import BadgeTooltip from 'components/BadgeTooltip/BadgeTooltip';
import { useConfetti } from 'hooks/useConfetti';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './SidebarBadgeDisplay.css';

interface SidebarBadgeDisplayProps {
  userId: number;
  isOwnProfile: boolean;
  onViewAll: () => void;
}

export interface SidebarBadgeDisplayHandle {
  triggerConfetti: () => void;
}

const SidebarBadgeDisplay = forwardRef<SidebarBadgeDisplayHandle, SidebarBadgeDisplayProps>(({
  userId,
  isOwnProfile,
  onViewAll,
}, ref) => {
  const { data: summary } = useGetBadgeSummaryQuery(userId);
  const { data: allEarned } = useGetUserBadgesQuery(userId);
  const [hoveredBadge, setHoveredBadge] = useState<{ badge: Badge; earned: boolean; earnedAt?: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const badgesContainerRef = useRef<HTMLDivElement>(null);
  const { triggerShowcase } = useConfetti();

  useImperativeHandle(ref, () => ({
    triggerConfetti: () => {
      const el = badgesContainerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      triggerShowcase({
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      });
    },
  }));

  const totalEarned = allEarned?.length ?? 0;

  // Hide entirely if user has 0 earned badges
  if (!summary || totalEarned === 0) {
    return null;
  }

  const summaryDisplayed = summary.displayedBadges ?? [];
  const maxBadgeSlots = summary.maxBadgeSlots ?? 1;

  const displayedBadges = summaryDisplayed.length > 0
    ? summaryDisplayed
    : (allEarned ?? []).slice(0, maxBadgeSlots);

  // Use 64px for 1-3 badges, scale down to 56px for 4 badges to fit in sidebar
  const badgeSize = displayedBadges.length >= 4 ? 56 : 64;

  const handleBadgeMouseEnter = (e: React.MouseEvent, ub: UserBadge) => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredBadge({ badge: ub.badge, earned: true, earnedAt: ub.earnedAt });
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }, 200);
  };

  const handleBadgeMouseMove = (e: React.MouseEvent) => {
    if (hoveredBadge) {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleBadgeMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHoveredBadge(null);
  };

  return (
    <>
      <div className="sidebar-badge-display">
        <div className="sidebar-badge-display__header">
          <span className="sidebar-badge-display__title">Trophy Case</span>
          <button className="sidebar-badge-display__view-all" onClick={onViewAll}>
            View All
          </button>
        </div>

        <div className="sidebar-badge-display__badges" ref={badgesContainerRef}>
          {displayedBadges.map((ub) => (
            <div
              key={ub.id}
              className="sidebar-badge-display__badge"
              onClick={onViewAll}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => handleBadgeMouseEnter(e, ub)}
              onMouseMove={handleBadgeMouseMove}
              onMouseLeave={handleBadgeMouseLeave}
            >
              <BadgeIcon
                icon={ub.badge.icon}
                shape={ub.badge.shape}
                tier={TIER_FROM_LEVEL[ub.badge.level] || 'bronze'}
                size={badgeSize}
                earned
              />
            </div>
          ))}
          {isOwnProfile && Array.from({ length: maxBadgeSlots - displayedBadges.length }, (_, i) => (
            <button
              key={`empty-${i}`}
              className="sidebar-badge-display__empty-slot"
              style={{ width: badgeSize, height: badgeSize }}
              onClick={onViewAll}
              title="Add a badge to your showcase"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          ))}
        </div>
      </div>

      {hoveredBadge && (
        <BadgeTooltip
          badge={hoveredBadge.badge}
          earned={hoveredBadge.earned}
          earnedAt={hoveredBadge.earnedAt}
          position={tooltipPosition}
        />
      )}
    </>
  );
});

SidebarBadgeDisplay.displayName = 'SidebarBadgeDisplay';

export default SidebarBadgeDisplay;
